/*eslint-disable*/
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { getData, postData, putData } from "utils/Gateway";
import * as url from "helper/UrlHelper";

// ==================== ADMIN POPUP ====================
const ApiAdminPopup = ({
  selectedApiDocId,
  setSelectedApiDocId = () => {},
  onSaveSuccess = () => {}, // callback to refresh parent list after save
}) => {
  const { t } = useTranslation();
  const token = localStorage.getItem("token");
  const userInfo = JSON.parse(localStorage.getItem("userInfo"));

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // ─────────────────────────────────────────────
  // Form state
  // ─────────────────────────────────────────────
  const [formData, setFormData] = useState({
    method: "POST",
    title: "",
    module: "",
    submodule: "",
    version: "1.0.0",
    endpoint: "",
    description: "",
    requiresAuth: true,
    APIstatus: true,
    queryParams: [],
    requestBodyParams: [],
    successResponse: "",
    errorCodes: [],
  });

  // ─────────────────────────────────────────────
  // Reset form
  // ─────────────────────────────────────────────
  const resetForm = () => {
    setFormData({
      method: "POST",
      title: "",
      module: "",
      submodule: "",
      version: "1.0.0",
      endpoint: "",
      description: "",
      requiresAuth: true,
      APIstatus: true,
      queryParams: [],
      requestBodyParams: [],
      successResponse: "",
      errorCodes: [],
    });
    setSaveError(null);
    setSelectedApiDocId(null);
  };

  // ─────────────────────────────────────────────
  // Handle generic input changes
  // ─────────────────────────────────────────────
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // ─────────────────────────────────────────────
  // Mappers — UI <-> Backend field name conversion
  // ─────────────────────────────────────────────
  const mapParamToBackend = (param) => ({
    fieldname: param.fieldName || "",
    fieldtype: param.type || "String",
    required: !!param.required,
    description: param.description || "",
  });

  const mapParamFromBackend = (param) => ({
    fieldName: param.fieldname || "",
    type: param.fieldtype || "String",
    required: !!param.required,
    description: param.description || "",
  });

  const mapErrorCodeToBackend = (err) => ({
    code: err.code || "",
    description: err.description || "",
    response: err.response || "",
  });

  const mapErrorCodeFromBackend = (err) => ({
    code: err.code || "",
    description: err.description || "",
    response: err.response || "",
  });

  // ─────────────────────────────────────────────
  // Query Parameters handlers
  // ─────────────────────────────────────────────
  const addQueryParam = () => {
    setFormData((prev) => ({
      ...prev,
      queryParams: [
        ...prev.queryParams,
        { fieldName: "", type: "String", required: false, description: "" },
      ],
    }));
  };

  const updateQueryParam = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      queryParams: prev.queryParams.map((param, i) =>
        i === index ? { ...param, [field]: value } : param,
      ),
    }));
  };

  const removeQueryParam = (index) => {
    setFormData((prev) => ({
      ...prev,
      queryParams: prev.queryParams.filter((_, i) => i !== index),
    }));
  };

  // ─────────────────────────────────────────────
  // Request Body Parameters handlers
  // ─────────────────────────────────────────────
  const addRequestBodyParam = () => {
    setFormData((prev) => ({
      ...prev,
      requestBodyParams: [
        ...prev.requestBodyParams,
        { fieldName: "", type: "String", required: false, description: "" },
      ],
    }));
  };

  const updateRequestBodyParam = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      requestBodyParams: prev.requestBodyParams.map((param, i) =>
        i === index ? { ...param, [field]: value } : param,
      ),
    }));
  };

  const removeRequestBodyParam = (index) => {
    setFormData((prev) => ({
      ...prev,
      requestBodyParams: prev.requestBodyParams.filter((_, i) => i !== index),
    }));
  };

  // ─────────────────────────────────────────────
  // Error Codes handlers
  // ─────────────────────────────────────────────
  const addErrorCode = () => {
    setFormData((prev) => ({
      ...prev,
      errorCodes: [
        ...prev.errorCodes,
        { code: "", description: "", response: "" },
      ],
    }));
  };

  const updateErrorCode = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      errorCodes: prev.errorCodes.map((error, i) =>
        i === index ? { ...error, [field]: value } : error,
      ),
    }));
  };

  const removeErrorCode = (index) => {
    setFormData((prev) => ({
      ...prev,
      errorCodes: prev.errorCodes.filter((_, i) => i !== index),
    }));
  };

  // ─────────────────────────────────────────────
  // Fetch API Doc details by ID and populate form
  // ─────────────────────────────────────────────
  const getApiDocDetails = async () => {
    if (!selectedApiDocId || selectedApiDocId === "new") return;

    try {
      setLoading(true);
      setSaveError(null);

      const requestUrl =
        url.API_BASE_URL +
        url.API_GET_API_DOC_DETAILS +
        `/${selectedApiDocId}`;

      const response = await getData(requestUrl);

      if (response.status) {
        const doc = response.data;

        setFormData({
          method: doc.method || "POST",
          title: doc.title || "",
          module: doc.module || "",
          submodule: doc.submodule || "",
          version: doc.version || "1.0.0",
          endpoint: doc.endpoint || "",
          description: doc.description || "",
          requiresAuth: doc.requiresauth ?? true,
          APIstatus: doc.APIstatus ?? true,
          queryParams: (doc.queryparams || []).map(mapParamFromBackend),
          requestBodyParams: (doc.requestbodyparams || []).map(
            mapParamFromBackend,
          ),
          successResponse: doc.successresponse || "",
          errorCodes: (doc.errorcodes || []).map(mapErrorCodeFromBackend),
        });
      } else {
        setSaveError(response.message || "Failed to fetch API doc details.");
      }
    } catch (error) {
      setSaveError("Something went wrong while fetching details.");
      console.error("[ApiAdminPopup] getApiDocDetails error:", error);
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────
  // Save handler — Create or Update
  // ─────────────────────────────────────────────
  const saveApiDocHandler = async () => {
    // Client-side validation
    if (!formData.title.trim()) {
      setSaveError("API Title is required.");
      return;
    }
    if (!formData.endpoint.trim()) {
      setSaveError("Endpoint is required.");
      return;
    }

    try {
      setSaving(true);
      setSaveError(null);

      // Build backend payload — map UI field names to schema field names
      const payload = {
        method: formData.method,
        title: formData.title.trim(),
        module: formData.module.trim(),
        submodule: formData.submodule.trim(),
        version: formData.version.trim() || "1.0.0",
        endpoint: formData.endpoint.trim(),
        description: formData.description.trim(),
        requiresauth: formData.requiresAuth,
        APIstatus: formData.APIstatus,
        queryparams: formData.queryParams.map(mapParamToBackend),
        requestbodyparams: formData.requestBodyParams.map(mapParamToBackend),
        successresponse: formData.successResponse.trim(),
        errorcodes: formData.errorCodes.map(mapErrorCodeToBackend),
      };

      let response;

      let requestUrl = url.API_BASE_URL;

      if (selectedApiDocId) {
        // UPDATE existing doc
        requestUrl +=
          url.API_UPDATE_API_DOC + `/${selectedApiDocId}`;
        response = await putData(requestUrl, payload);
      } else {
        // CREATE new doc
        requestUrl += url.API_CREATE_API_DOC;
        response = await postData(requestUrl, payload);
      }

      if (response.status) {
        // Close offcanvas via Bootstrap JS API
        const offcanvasEl = document.getElementById("apiAdminPopup");
        if (offcanvasEl) {
          const bsOffcanvas =
            window.bootstrap?.Offcanvas.getInstance(offcanvasEl);
          bsOffcanvas?.hide();
        }

        resetForm();
        onSaveSuccess(); // notify parent to refresh list
      } else {
        setSaveError(response.message || "Failed to save API doc.");
      }
    } catch (error) {
      setSaveError("Something went wrong while saving.");
      console.error("[ApiAdminPopup] saveApiDocHandler error:", error);
    } finally {
      setSaving(false);
    }
  };

  // ─────────────────────────────────────────────
  // Load details when selectedApiDocId changes
  // ─────────────────────────────────────────────
  useEffect(() => {
    if (selectedApiDocId && selectedApiDocId !== "new") {
      getApiDocDetails();
    } else {
      resetForm();
    }
  }, [selectedApiDocId]);

  // ─────────────────────────────────────────────
  // Constants
  // ─────────────────────────────────────────────
  const methodOptions = ["GET", "POST", "PUT", "DELETE", "PATCH"];
  const typeOptions = [
    "String",
    "ObjectId",
    "Number",
    "Boolean",
    "Array",
    "Object",
    "Date",
  ];

  // ─────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────
  return (
    <div
      className="offcanvas lg offcanvas-end bg-white border-0 shadow-lg"
      tabIndex="-1"
      id="apiAdminPopup"
      aria-labelledby="apiAdminPopupLabel"
    >
      {/* ── Header ── */}
      <div className="offcanvas-header bg-primary text-white p-4 pe-5 pb-3 border-bottom border-gray-300">
        <div className="d-flex align-items-center gap-3">
          <div className="icon-wrapper p-3 bg-white rounded-circle d-flex align-items-center justify-content-center">
            <span className="material-symbols-outlined text-primary fs-5 icon-md">
              api
            </span>
          </div>
          <div>
            <h3
              className="offcanvas-title fw-bold text-white mb-0"
              id="apiAdminPopupLabel"
            >
              {selectedApiDocId && selectedApiDocId !== "new"
                ? t("Edit API Documentation")
                : t("Add New API Documentation")}
            </h3>
            <p className="text-white fs-sm mb-0">
              {selectedApiDocId && selectedApiDocId !== "new"
                ? t("Update API endpoint details")
                : t("Create new API endpoint documentation")}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="btn-close p-1 bg-gray-200 rounded-circle shadow-sm border-0"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
          onClick={resetForm}
        ></button>
      </div>

      {/* ── Body ── */}
      <div className="offcanvas-body p-4">
        {loading ? (
          // Loading spinner while fetching details
          <div className="d-flex justify-content-center align-items-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <div className="p-3 p-md-4 border border-gray-300 rounded-10 shadow-sm bg-light">
            {/* Error alert */}
            {saveError && (
              <div
                className="alert alert-danger alert-dismissible fade show"
                role="alert"
              >
                <span className="material-symbols-outlined me-2">error</span>
                {saveError}
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setSaveError(null)}
                ></button>
              </div>
            )}

            {/* ── Basic Information ── */}
            <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
              <h5 className="fw-bold mb-3 pb-2 border-bottom">
                {t("Basic Information")}
              </h5>

              {/* Method + Version */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {t("HTTP Method")} <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select border border-gray-300 shadow-none"
                    value={formData.method}
                    onChange={(e) =>
                      handleInputChange("method", e.target.value)
                    }
                  >
                    {methodOptions.map((method) => (
                      <option key={method} value={method}>
                        {method}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {t("Version")}
                  </label>
                  <input
                    type="text"
                    className="form-control border border-gray-300 shadow-none"
                    placeholder="e.g., 1.0.0"
                    value={formData.version}
                    onChange={(e) =>
                      handleInputChange("version", e.target.value)
                    }
                  />
                </div>
              </div>

              {/* Module + Submodule */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {t("Module")}
                  </label>
                  <input
                    type="text"
                    className="form-control border border-gray-300 shadow-none"
                    placeholder={t("e.g., Lead")}
                    value={formData.module}
                    onChange={(e) =>
                      handleInputChange("module", e.target.value)
                    }
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {t("Submodule")}
                  </label>
                  <input
                    type="text"
                    className="form-control border border-gray-300 shadow-none"
                    placeholder={t("e.g., Purchase")}
                    value={formData.submodule}
                    onChange={(e) =>
                      handleInputChange("submodule", e.target.value)
                    }
                  />
                </div>
              </div>

              {/* Requires Auth + API Status */}
              <div className="row mb-3">
                <div className="col-md-6 d-flex align-items-center">
                  <div className="form-check d-flex align-items-center gap-1">
                    <input
                      className="form-check-input border border-gray-300 shadow-none"
                      type="checkbox"
                      id="requiresAuth"
                      checked={formData.requiresAuth}
                      onChange={(e) =>
                        handleInputChange("requiresAuth", e.target.checked)
                      }
                    />
                    <label
                      className="form-check-label fw-semibold mb-0 ms-1"
                      htmlFor="requiresAuth"
                    >
                      {t("Requires Authorization")}
                    </label>
                  </div>
                </div>
                <div className="col-md-6 d-flex align-items-center">
                  <div className="form-check form-switch p-0 m-0 d-flex align-items-center gap-2">
                    <input
                      className="form-check-input border border-gray-300 shadow-none ms-0"
                      type="checkbox"
                      role="switch"
                      id="APIstatus"
                      checked={formData.APIstatus}
                      onChange={(e) =>
                        handleInputChange("APIstatus", e.target.checked)
                      }
                    />
                    <label
                      className="form-check-label fw-semibold mb-0"
                      htmlFor="APIstatus"
                    >
                      {formData.APIstatus ? t("API Active") : t("API Inactive")}
                    </label>
                  </div>
                </div>
              </div>

              {/* Title */}
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  {t("API Title")} <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control border border-gray-300 shadow-none"
                  placeholder={t("e.g., Purchase Lead")}
                  value={formData.title}
                  onChange={(e) => handleInputChange("title", e.target.value)}
                />
              </div>

              {/* Endpoint */}
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  {t("Endpoint")} <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control border border-gray-300 shadow-none"
                  placeholder={t("e.g., /api/Lead/purchaseLead")}
                  value={formData.endpoint}
                  onChange={(e) =>
                    handleInputChange("endpoint", e.target.value)
                  }
                />
              </div>

              {/* Description */}
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  {t("Description")}
                </label>
                <textarea
                  className="form-control border border-gray-300 shadow-none"
                  rows="3"
                  placeholder={t("Describe what this API endpoint does...")}
                  value={formData.description}
                  onChange={(e) =>
                    handleInputChange("description", e.target.value)
                  }
                ></textarea>
              </div>
            </div>

            {/* ── Query Parameters ── */}
            <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
              <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                <h5 className="fw-bold mb-0">{t("Query Parameters")}</h5>
                <button
                  type="button"
                  className="btn btn-sm btn-primary d-flex align-items-center gap-2"
                  onClick={addQueryParam}
                >
                  <span
                    className="material-symbols-outlined me-1"
                    style={{ fontSize: "18px" }}
                  >
                    add
                  </span>
                  {t("Add Parameter")}
                </button>
              </div>

              {formData.queryParams.length === 0 ? (
                <p className="text-muted small">
                  {t("No query parameters added yet.")}
                </p>
              ) : (
                <div className="params-list">
                  {formData.queryParams.map((param, index) => (
                    <div key={index} className="card mb-3 border">
                      <div className="card-body p-3">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <h6 className="mb-0 text-primary">
                            Parameter {index + 1}
                          </h6>
                          <button
                            type="button"
                            className="btn btn-sm btn-link text-danger p-0"
                            onClick={() => removeQueryParam(index)}
                          >
                            <span
                              className="material-symbols-outlined"
                              style={{ fontSize: "20px" }}
                            >
                              delete
                            </span>
                          </button>
                        </div>
                        <div className="row g-2">
                          <div className="col-md-4">
                            <label className="form-label small fw-semibold">
                              {t("Field Name")}
                            </label>
                            <input
                              type="text"
                              className="form-control form-control-sm bg-white border border-gray-300 shadow-none"
                              placeholder={t("e.g., userId")}
                              value={param.fieldName}
                              onChange={(e) =>
                                updateQueryParam(
                                  index,
                                  "fieldName",
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label small fw-semibold">
                              {t("Type")}
                            </label>
                            <select
                              className="form-select form-select-sm bg-white border border-gray-300 shadow-none"
                              value={param.type}
                              onChange={(e) =>
                                updateQueryParam(index, "type", e.target.value)
                              }
                            >
                              {typeOptions.map((type) => (
                                <option key={type} value={type}>
                                  {type}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-md-2 d-flex align-items-end">
                            <div className="form-check">
                              <input
                                className="form-check-input bg-white border border-gray-300 shadow-none"
                                type="checkbox"
                                id={`queryRequired${index}`}
                                checked={param.required}
                                onChange={(e) =>
                                  updateQueryParam(
                                    index,
                                    "required",
                                    e.target.checked,
                                  )
                                }
                              />
                              <label
                                className="form-check-label small ms-1"
                                htmlFor={`queryRequired${index}`}
                              >
                                {t("Required")}
                              </label>
                            </div>
                          </div>
                          <div className="col-12">
                            <label className="form-label small fw-semibold">
                              {t("Description")}
                            </label>
                            <textarea
                              className="form-control form-control-sm bg-white border border-gray-300 shadow-none"
                              placeholder={t("Describe this parameter...")}
                              value={param.description}
                              onChange={(e) =>
                                updateQueryParam(
                                  index,
                                  "description",
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── Request Body Parameters ── */}
            <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
              <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                <h5 className="fw-bold mb-0">{t("Request Body Parameters")}</h5>
                <button
                  type="button"
                  className="btn btn-sm btn-primary d-flex align-items-center gap-2"
                  onClick={addRequestBodyParam}
                >
                  <span
                    className="material-symbols-outlined me-1"
                    style={{ fontSize: "18px" }}
                  >
                    add
                  </span>
                  {t("Add Parameter")}
                </button>
              </div>

              {formData.requestBodyParams.length === 0 ? (
                <p className="text-muted small">
                  {t("No request body parameters added yet.")}
                </p>
              ) : (
                <div className="params-list">
                  {formData.requestBodyParams.map((param, index) => (
                    <div key={index} className="card mb-3 border">
                      <div className="card-body p-3">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <h6 className="mb-0 text-primary">
                            Parameter {index + 1}
                          </h6>
                          <button
                            type="button"
                            className="btn btn-sm btn-link text-danger p-0"
                            onClick={() => removeRequestBodyParam(index)}
                          >
                            <span
                              className="material-symbols-outlined"
                              style={{ fontSize: "20px" }}
                            >
                              delete
                            </span>
                          </button>
                        </div>
                        <div className="row g-2">
                          <div className="col-md-4">
                            <label className="form-label small fw-semibold">
                              {t("Field Name")}
                            </label>
                            <input
                              type="text"
                              className="form-control form-control-sm bg-white border border-gray-300 shadow-none"
                              placeholder={t("e.g., leadid")}
                              value={param.fieldName}
                              onChange={(e) =>
                                updateRequestBodyParam(
                                  index,
                                  "fieldName",
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label small fw-semibold">
                              {t("Type")}
                            </label>
                            <select
                              className="form-select form-select-sm bg-white border border-gray-300 shadow-none"
                              value={param.type}
                              onChange={(e) =>
                                updateRequestBodyParam(
                                  index,
                                  "type",
                                  e.target.value,
                                )
                              }
                            >
                              {typeOptions.map((type) => (
                                <option key={type} value={type}>
                                  {type}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-md-2 d-flex align-items-end">
                            <div className="form-check">
                              <input
                                className="form-check-input bg-white border border-gray-300 shadow-none"
                                type="checkbox"
                                id={`bodyRequired${index}`}
                                checked={param.required}
                                onChange={(e) =>
                                  updateRequestBodyParam(
                                    index,
                                    "required",
                                    e.target.checked,
                                  )
                                }
                              />
                              <label
                                className="form-check-label small ms-1"
                                htmlFor={`bodyRequired${index}`}
                              >
                                {t("Required")}
                              </label>
                            </div>
                          </div>
                          <div className="col-12">
                            <label className="form-label small fw-semibold">
                              {t("Description")}
                            </label>
                            <textarea
                              className="form-control form-control-sm bg-white border border-gray-300 shadow-none"
                              placeholder={t("Describe this parameter...")}
                              value={param.description}
                              onChange={(e) =>
                                updateRequestBodyParam(
                                  index,
                                  "description",
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── Success Response ── */}
            <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
              <h5 className="fw-bold mb-3 pb-2 border-bottom">
                {t("Success Response")}
              </h5>
              <label className="form-label fw-semibold">
                {t("Response JSON")}
              </label>
              <textarea
                className="form-control font-monospace border border-gray-300 shadow-none"
                rows="8"
                placeholder={t(
                  'Paste JSON response here...\n{\n  "status": true,\n  "data": {...},\n  "message": "Success"\n}',
                )}
                value={formData.successResponse}
                onChange={(e) =>
                  handleInputChange("successResponse", e.target.value)
                }
                style={{ fontSize: "13px" }}
              ></textarea>
            </div>

            {/* ── Error Codes ── */}
            <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
              <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                <h5 className="fw-bold mb-0">{t("Error Codes")}</h5>
                <button
                  type="button"
                  className="btn btn-sm btn-primary d-flex align-items-center gap-2"
                  onClick={addErrorCode}
                >
                  <span
                    className="material-symbols-outlined me-1"
                    style={{ fontSize: "18px" }}
                  >
                    add
                  </span>
                  {t("Add Error Code")}
                </button>
              </div>

              {formData.errorCodes.length === 0 ? (
                <p className="text-muted small">
                  {t("No error codes added yet.")}
                </p>
              ) : (
                <div className="error-codes-list">
                  {formData.errorCodes.map((error, index) => (
                    <div key={index} className="card mb-3 border">
                      <div className="card-body p-3">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <h6 className="mb-0 text-danger">
                            Error {index + 1}
                          </h6>
                          <button
                            type="button"
                            className="btn btn-sm btn-link text-danger p-0"
                            onClick={() => removeErrorCode(index)}
                          >
                            <span
                              className="material-symbols-outlined"
                              style={{ fontSize: "20px" }}
                            >
                              delete
                            </span>
                          </button>
                        </div>
                        <div className="row g-2">
                          <div className="col-md-3">
                            <label className="form-label small fw-semibold">
                              {t("Error Code")}
                            </label>
                            <input
                              type="text"
                              className="form-control form-control-sm bg-white border border-gray-300 shadow-none"
                              placeholder={t("e.g., 203, 404")}
                              value={error.code}
                              onChange={(e) =>
                                updateErrorCode(index, "code", e.target.value)
                              }
                            />
                          </div>
                          <div className="col-md-9">
                            <label className="form-label small fw-semibold">
                              {t("Description")}
                            </label>
                            <input
                              type="text"
                              className="form-control form-control-sm bg-white border border-gray-300 shadow-none"
                              placeholder={t("e.g., Lead not found")}
                              value={error.description}
                              onChange={(e) =>
                                updateErrorCode(
                                  index,
                                  "description",
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                          <div className="col-12">
                            <label className="form-label small fw-semibold">
                              {t("Response JSON")}
                            </label>
                            <textarea
                              className="form-control form-control-sm font-monospace bg-white border border-gray-300 shadow-none"
                              rows="4"
                              placeholder={t("Paste error response JSON...")}
                              value={error.response}
                              onChange={(e) =>
                                updateErrorCode(
                                  index,
                                  "response",
                                  e.target.value,
                                )
                              }
                              style={{ fontSize: "13px" }}
                            ></textarea>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Sticky Footer ── */}
      <div
        className="offcanvas-footer p-3 bg-white border-top d-flex justify-content-end gap-2"
        style={{ position: "sticky", bottom: 0, zIndex: 10 }}
      >
        <button
          type="button"
          className="btn btn-outline-secondary"
          data-bs-dismiss="offcanvas"
          onClick={resetForm}
        >
          {t("Cancel")}
        </button>

        <button
          type="button"
          className="btn btn-primary d-flex align-items-center gap-2"
          disabled={saving || loading || !formData.title || !formData.endpoint}
          onClick={saveApiDocHandler}
          style={{ cursor: saving ? "not-allowed" : "pointer" }}
        >
          {saving ? (
            <>
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
              {t("Saving...")}
            </>
          ) : (
            <>
              <span
                className="material-symbols-outlined me-1"
                style={{ fontSize: "18px" }}
              >
                save
              </span>
              {selectedApiDocId && selectedApiDocId !== "new"
                ? t("Update API")
                : t("Create API")}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ApiAdminPopup;
